/**
 * Variable-font headline with interactive mouse tracking.
 * Each character gets individual font-variation-settings (weight + width)
 * based on a chosen pattern (wave, random, edges, etc.). On hover, nearby
 * characters warp toward the cursor via direct DOM manipulation (no re-renders).
 */
import { motion } from "framer-motion";
import { useMemo, useRef, useCallback } from "react";

interface VariableHeadlineProps {
  children: string;
  className?: string;
  as?: "h1" | "h2" | "h3";
  variant?: "animated" | "image";
  imageSrc?: string;
  pattern?: "wave" | "random" | "edges" | "center" | "alternating" | "crescendo";
}

const generateVariation = (
  index: number, 
  total: number, 
  pattern: string,
): { weight: number; width: number } => {
  switch (pattern) {
    case "wave": {
      const progress = index / Math.max(total - 1, 1);
      const wave = Math.sin(progress * Math.PI * 2);
      return { weight: 500 + wave * 400, width: 90 + wave * 35 };
    }
    case "random": {
      const s = (index * 7919) % 100;
      const weightBias = s > 50 ? 0.7 : 0.3;
      return { weight: 100 + weightBias * 800, width: 62 + (s / 100) * 63 };
    }
    case "edges": {
      const d = Math.abs((index / Math.max(total - 1, 1)) - 0.5) * 2;
      const c = Math.pow(d, 0.5);
      return { weight: 200 + c * 700, width: 70 + c * 55 };
    }
    case "center": {
      const cp = 1 - Math.abs((index / Math.max(total - 1, 1)) - 0.5) * 2;
      const cc = Math.pow(cp, 0.5);
      return { weight: 100 + cc * 800, width: 62 + cc * 63 };
    }
    case "alternating": {
      const isEven = index % 2 === 0;
      const isThird = index % 3 === 0;
      return { weight: isEven ? 900 : (isThird ? 100 : 400), width: isEven ? 125 : (isThird ? 62 : 90) };
    }
    case "crescendo": {
      const p = index / Math.max(total - 1, 1);
      const e = Math.pow(p, 1.5);
      return { weight: 100 + e * 800, width: 62 + e * 63 };
    }
    default:
      return { weight: 700, width: 100 };
  }
};

const VariableHeadline = ({
  children,
  className = "",
  as: Component = "h1",
  variant = "animated",
  imageSrc,
  pattern = "wave",
}: VariableHeadlineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spanRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Split into words, preserving character-level variations with a global index
  const words = useMemo(() => {
    const result: { char: string; variation: ReturnType<typeof generateVariation> }[][] = [];
    let currentWord: typeof result[0] = [];
    let globalIndex = 0;
    const total = children.length;

    for (const char of children) {
      if (char === " ") {
        if (currentWord.length > 0) {
          result.push(currentWord);
          currentWord = [];
        }
        // Push space as its own "word"
        result.push([{ char: " ", variation: generateVariation(globalIndex, total, pattern) }]);
      } else {
        currentWord.push({ char, variation: generateVariation(globalIndex, total, pattern) });
      }
      globalIndex++;
    }
    if (currentWord.length > 0) result.push(currentWord);
    return result;
  }, [children, pattern]);

  // Direct DOM updates — no setState, no re-renders
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const maxRadius = 150;

    for (let i = 0; i < spanRefs.current.length; i++) {
      const span = spanRefs.current[i];
      if (!span) continue;
      // Remove transition during hover for instant response
      span.style.transition = 'none';
      const sr = span.getBoundingClientRect();
      const cx = sr.left - rect.left + sr.width / 2;
      const cy = sr.top - rect.top + sr.height / 2;
      const dist = Math.sqrt((mx - cx) ** 2 + (my - cy) ** 2);
      const influence = Math.max(0, 1 - dist / maxRadius);
      const eased = Math.pow(influence, 0.5);
      const w = 300 + eased * 600;
      const wd = 75 + eased * 50;
      span.style.fontVariationSettings = `'wght' ${w}, 'wdth' ${wd}`;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Preserve the style the user created — do nothing
  }, []);

  const baseStyles = `uppercase tracking-[-0.02em]`;
  const cutoutStyles = `bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]`;
  const animatedBackground = `bg-[length:400%_400%] bg-gradient-to-r from-foreground via-muted-foreground to-foreground animate-[gradient-shift_8s_ease-in-out_infinite]`;

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Component
        className={`
          ${baseStyles}
          ${cutoutStyles}
          ${variant === "animated" ? animatedBackground : ""}
          ${className}
        `}
        style={
          variant === "image" && imageSrc
            ? { backgroundImage: `url(${imageSrc})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
      >
        {words.map((word, wIndex) => {
          const isSpace = word.length === 1 && word[0].char === " ";
          if (isSpace) {
            return <span key={`s-${wIndex}`} style={{ whiteSpace: "pre" }}> </span>;
          }
          return (
            <span key={`w-${wIndex}`} className="inline-flex">
              {word.map(({ char, variation }, cIndex) => (
                <span
                  key={cIndex}
                  ref={(el) => {
                    // Calculate global index for ref
                    let gi = 0;
                    for (let w = 0; w < wIndex; w++) gi += words[w].length;
                    gi += cIndex;
                    spanRefs.current[gi] = el;
                  }}
                  className="inline-block"
                  style={{
                    fontFamily: "'Archivo Variable', 'Archivo', sans-serif",
                    fontVariationSettings: `'wght' ${variation.weight}, 'wdth' ${variation.width}`,
                  }}
                >
                  {char}
                </span>
              ))}
            </span>
          );
        })}
      </Component>
    </motion.div>
  );
};

export default VariableHeadline;
